import React, { useState, useEffect, useRef } from "react";
import { Button, Container, Row, Col } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import HeaderChatBoat from "./HeaderChatBoat";
import envConfig from "../envConfig";

// ---- Lightweight markdown renderer (handles ### headings, **bold**, - lists) ----

function renderInline(str) {
  const parts = str.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i}>{part.slice(2, -2)}</strong>
      : part
  );
}

function MarkdownText({ text }) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let listItems = [];
  let keyCounter = 0;
  const k = () => keyCounter++;

  const flushList = () => {
    if (listItems.length) {
      elements.push(<ul key={k()} className="mb-2 ps-3" style={{ listStyleType: "disc" }}>{listItems}</ul>);
      listItems = [];
    }
  };

  lines.forEach((line) => {
    const t = line.trim();
    if (/^###\s/.test(t)) {
      flushList();
      elements.push(<h6 key={k()} className="fw-bold mt-3 mb-1">{renderInline(t.replace(/^###\s/, ""))}</h6>);
    } else if (/^##\s/.test(t)) {
      flushList();
      elements.push(<h5 key={k()} className="fw-bold mt-3 mb-1">{renderInline(t.replace(/^##\s/, ""))}</h5>);
    } else if (/^[-*]\s/.test(t)) {
      listItems.push(<li key={k()} className="mb-1">{renderInline(t.slice(2))}</li>);
    } else if (t === "") {
      flushList();
    } else {
      flushList();
      elements.push(<p key={k()} className="mb-1">{renderInline(t)}</p>);
    }
  });

  flushList();
  return <div>{elements}</div>;
}

// ---- Response renderers ----

function LocationBadge({ label }) {
  if (!label || label === "No location selected") return null;
  return <p className="text-muted small mb-2">📍 {label}</p>;
}

function riskBadgeClass(risk = "") {
  const r = risk.toUpperCase();
  if (r.includes("EMERGENCY")) return "badge bg-danger";
  if (r.includes("HIGH")) return "badge bg-warning text-dark";
  if (r.includes("INTERMEDIATE") || r.includes("MEDIUM")) return "badge bg-info text-dark";
  return "badge bg-success";
}

function fmtDate(iso) {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  } catch { return iso; }
}

function round1(v) {
  if (v == null) return "-";
  return typeof v === "number" ? v.toFixed(1) : v;
}

function CountResponseDisplay({ data, locationLabel }) {
  const meta = data.metadata || {};
  const entity = data.entity_type || "records";
  const scope = meta.admin_level !== "all" ? `${meta.admin_level} level` : "all locations";
  return (
    <div className="scrollBox">
      <LocationBadge label={locationLabel} />
      <div className="d-flex align-items-baseline gap-2 mb-2">
        <span style={{ fontSize: "2em", fontWeight: "bold", lineHeight: 1 }}>{data.count}</span>
        <span className="text-muted">{entity}</span>
      </div>
      <p className="mb-1 small">
        Counted at <strong>{scope}</strong> in <strong>{meta.country_name}</strong>
        {meta.filter?.includes("ref_date") && (
          <span className="text-muted"> · {meta.filter.split(", ").find(p => p.startsWith("ref_date"))}</span>
        )}
      </p>
    </div>
  );
}

function AdvisoryResponseDisplay({ data, locationLabel }) {
  const meta = data.metadata || {};
  return (
    <div className="scrollBox">
      <LocationBadge label={locationLabel} />
      {(meta.date_range || meta.admin_level) && (
        <p className="text-muted small mb-2">
          {meta.admin_level && meta.admin_level !== "all" && <span>Scope: <strong>{meta.admin_level}</strong> · </span>}
          {meta.date_range && <span>{meta.date_range}</span>}
        </p>
      )}
      {data.summary && (
        <div className="mb-3">
          <MarkdownText text={data.summary} />
        </div>
      )}
      {data.advisories && data.advisories.length > 0 ? (
        <>
          <hr className="my-2" />
          <p className="text-muted small mb-2">{meta.total_records} record{meta.total_records !== 1 ? "s" : ""}</p>
          {data.advisories.map((adv, i) => (
            <div key={i} className="mb-2 pb-2 border-bottom">
              <div className="d-flex align-items-center gap-2 mb-1">
                <span className="small fw-semibold">{adv.ref_date}</span>
                <span className={riskBadgeClass(adv.risk_level)} style={{ fontSize: "0.7em" }}>
                  {adv.risk_level?.toUpperCase()}
                </span>
              </div>
              {(adv.temp_max != null || adv.temp_min != null) && (
                <p className="mb-1 small">
                  Temp: <strong>{adv.temp_min ?? "-"}°C – {adv.temp_max ?? "-"}°C</strong>
                  {adv.predicted_pond_temperature != null && (
                    <span className="text-muted"> · Pond: {adv.predicted_pond_temperature}°C</span>
                  )}
                </p>
              )}
            </div>
          ))}
        </>
      ) : (
        <p className="text-muted small">No advisories found for the specified criteria.</p>
      )}
    </div>
  );
}

function ForecastResponseDisplay({ data, locationLabel }) {
  const successParams = (data.parameters || []).filter((p) => p.status === "success");
  if (successParams.length === 0) {
    return <div className="scrollBox"><p className="text-muted small">No data available for the requested parameters.</p></div>;
  }

  const firstParam = successParams[0];
  const isObserved = Array.isArray(firstParam.data) && !Array.isArray(firstParam.forecast);
  const meta = data.query_metadata || {};

  const getPoints = (param) =>
    isObserved ? (param.data || []) : (param.forecast || []).slice(0, 7);

  const getColHeader = (point) => fmtDate(point.date);

  const getValue = (point, paramCode) => {
    const raw = isObserved
      ? (paramCode === "temp_min" ? point.temp_min : paramCode === "temp_max" ? point.temp_max : point.rainfall)
      : point.value;
    return round1(raw);
  };

  const periodLabel = isObserved
    ? (meta.start_date && meta.end_date ? `${fmtDate(meta.start_date)} – ${fmtDate(meta.end_date)}` : meta.start_date ? fmtDate(meta.start_date) : "")
    : (meta.data_date ? `7-day forecast from ${fmtDate(meta.data_date)}` : "7-day forecast");

  const colPoints = getPoints(firstParam);

  return (
    <div className="scrollBox">
      <LocationBadge label={locationLabel} />
      <p className="text-muted small mb-2">
        {isObserved ? "Observed data" : "Forecast"}{periodLabel ? ` · ${periodLabel}` : ""}
        {meta.is_fallback && <span className="text-warning ms-2">{meta.fallback_info}</span>}
      </p>
      <div className="table-responsive">
        <table className="table tbl_new tdColor table-bordered table-sm mb-0">
          <thead>
            <tr>
              <th style={{ minWidth: "130px" }}>Parameter</th>
              {colPoints.map((p, i) => <td key={i} style={{ whiteSpace: "nowrap" }}>{getColHeader(p)}</td>)}
            </tr>
          </thead>
          <tbody>
            {successParams.map((param, i) => (
              <tr key={i}>
                <th className="text-nowrap">
                  {param.param_name}
                  {param.units ? <span className="text-muted fw-normal"> ({param.units})</span> : ""}
                </th>
                {getPoints(param).map((point, j) => (
                  <td key={j}>{getValue(point, param.param_code)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResponseDisplay({ data, locationLabel }) {
  if (!data) return null;
  if (data.query_type === "count") return <CountResponseDisplay data={data} locationLabel={locationLabel} />;
  if (data.query_type === "advisory_details") return <AdvisoryResponseDisplay data={data} locationLabel={locationLabel} />;
  if (data.status === "success" && data.parameters) return <ForecastResponseDisplay data={data} locationLabel={locationLabel} />;
  return <div className="scrollBox"><pre style={{ fontSize: "0.75em" }}>{JSON.stringify(data, null, 2)}</pre></div>;
}

// ---- Main ChatBox component ----

function ChatBox() {
  const routerLocation = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const messagesEndRef = useRef(null);
  const promptsRef = useRef(null);
  const isInitializing = useRef(true);
  const chatIdRef = useRef(null);

  const navState = routerLocation.state || {};

  // Read user details from localStorage to determine role and location assignment
  const [userDetails] = useState(() => {
    try { return JSON.parse(localStorage.getItem("userDetails") || "{}"); } catch { return {}; }
  });
  const isAdmin = userDetails.role_id === 1;

  const CHAT_KEY = `profishBlue_chats_${userDetails.user_id || "guest"}`;
  const [chats, setChats] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`profishBlue_chats_${userDetails.user_id || "guest"}`) || "[]"); } catch { return []; }
  });
  const [currentChatId, setCurrentChatId] = useState(null);

  // How deeply the user is scoped: 3=camp, 2=district, 1=province, 0=country, -1=admin (no lock)
  const lockedLevel = !isAdmin ? (
    userDetails.allowed_adm3_id ? 3 :
    userDetails.allowed_adm2_id ? 2 :
    userDetails.allowed_adm1_id ? 1 : 0
  ) : -1;

  // Location state — non-admin: seeded from userDetails; admin: seeded from nav state
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [camps, setCamps] = useState([]);

  const initCountry = !isAdmin && userDetails.allowed_country_id
    ? String(userDetails.allowed_country_id)
    : (navState.country_id ? String(navState.country_id) : "");
  const initProvince = !isAdmin && userDetails.allowed_adm1_id
    ? String(userDetails.allowed_adm1_id)
    : (navState.adm1_id ? String(navState.adm1_id) : "");
  const initDistrict = !isAdmin && userDetails.allowed_adm2_id
    ? String(userDetails.allowed_adm2_id)
    : (navState.adm2_id ? String(navState.adm2_id) : "");
  const initCamp = !isAdmin && userDetails.allowed_adm3_id
    ? String(userDetails.allowed_adm3_id)
    : (navState.adm3_id ? String(navState.adm3_id) : "");

  const [selectedCountry, setSelectedCountry] = useState(initCountry);
  const [selectedProvince, setSelectedProvince] = useState(initProvince);
  const [selectedDistrict, setSelectedDistrict] = useState(initDistrict);
  const [selectedCamp, setSelectedCamp] = useState(initCamp);

  const getHeaders = () => {
    const token = localStorage.getItem("profishBlue_authToken");
    return { "Content-Type": "application/json", Authorization: token };
  };

  const getOrCreateChatId = () => {
    if (!chatIdRef.current) {
      chatIdRef.current = Date.now().toString();
      setCurrentChatId(chatIdRef.current);
    }
    return chatIdRef.current;
  };

  const saveChat = (msgs) => {
    if (!msgs.length) return;
    const chatId = getOrCreateChatId();
    const title = msgs.find((m) => m.role === "user")?.text?.slice(0, 45) || "Untitled";
    const entry = {
      id: chatId, title, messages: msgs,
      country_id: selectedCountry, adm1_id: selectedProvince,
      adm2_id: selectedDistrict, adm3_id: selectedCamp,
      updatedAt: new Date().toISOString(),
    };
    setChats((prev) => {
      const updated = [entry, ...prev.filter((c) => c.id !== chatId)].slice(0, 10);
      localStorage.setItem(CHAT_KEY, JSON.stringify(updated));
      return updated;
    });
  };

  const loadChat = (chat) => {
    chatIdRef.current = chat.id;
    setCurrentChatId(chat.id);
    setMessages(chat.messages);
    setShowPrompts(false);
    isInitializing.current = true;
    setSelectedCountry(chat.country_id || "");
    setSelectedProvince(chat.adm1_id || "");
    setSelectedDistrict(chat.adm2_id || "");
    setSelectedCamp(chat.adm3_id || "");
    setTimeout(() => { isInitializing.current = false; }, 0);
  };

  const deleteChat = (e, chatId) => {
    e.stopPropagation();
    setChats((prev) => {
      const updated = prev.filter((c) => c.id !== chatId);
      localStorage.setItem(CHAT_KEY, JSON.stringify(updated));
      return updated;
    });
    if (chatIdRef.current === chatId) {
      chatIdRef.current = null;
      setCurrentChatId(null);
      setMessages([]);
    }
  };

  // Mark initializing as done after first render cycle so cascade resets only fire on user changes
  useEffect(() => {
    const t = setTimeout(() => { isInitializing.current = false; }, 0);
    return () => clearTimeout(t);
  }, []);

  // Fetch countries on mount
  useEffect(() => {
    fetch(`${envConfig.API_URL}/lookups/locations/country`, { headers: getHeaders() })
      .then((r) => r.json())
      .then((d) => {
        const list = d.data || [];
        setCountries(list);
        // For admins with no pre-selection, default to Zambia
        if (!selectedCountry && list.length > 0) {
          const zambia = list.find((c) => c.country?.toLowerCase() === "zambia");
          setSelectedCountry(String(zambia ? zambia.country_id : list[0].country_id));
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch provinces when country changes
  useEffect(() => {
    if (!selectedCountry) return;
    if (!isInitializing.current) {
      setProvinces([]);
      setDistricts([]);
      setCamps([]);
      setSelectedProvince("");
      setSelectedDistrict("");
      setSelectedCamp("");
    }
    fetch(`${envConfig.API_URL}/lookups/locations/adm1?country_id=${selectedCountry}`, { headers: getHeaders() })
      .then((r) => r.json())
      .then((d) => setProvinces(d.data || []))
      .catch(() => {});
  }, [selectedCountry]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch districts when province changes
  useEffect(() => {
    if (!selectedCountry || !selectedProvince) return;
    if (!isInitializing.current) {
      setDistricts([]);
      setCamps([]);
      setSelectedDistrict("");
      setSelectedCamp("");
    }
    fetch(`${envConfig.API_URL}/lookups/locations/adm2?country_id=${selectedCountry}&adm1_id=${selectedProvince}`, { headers: getHeaders() })
      .then((r) => r.json())
      .then((d) => setDistricts(d.data || []))
      .catch(() => {});
  }, [selectedProvince]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch camps when district changes
  useEffect(() => {
    if (!selectedCountry || !selectedProvince || !selectedDistrict) return;
    if (!isInitializing.current) {
      setCamps([]);
      setSelectedCamp("");
    }
    fetch(`${envConfig.API_URL}/lookups/locations/adm3?country_id=${selectedCountry}&adm1_id=${selectedProvince}&adm2_id=${selectedDistrict}`, { headers: getHeaders() })
      .then((r) => r.json())
      .then((d) => setCamps(d.data || []))
      .catch(() => {});
  }, [selectedDistrict]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close prompts panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (promptsRef.current && !promptsRef.current.contains(e.target)) {
        setShowPrompts(false);
      }
    };
    if (showPrompts) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showPrompts]);

  useEffect(() => {
    if (navState.initialQuestion) setInputText(navState.initialQuestion);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const locationLabel = () => {
    const country = countries.find((c) => String(c.country_id) === selectedCountry);
    const province = provinces.find((p) => String(p.adm1_id) === selectedProvince);
    const district = districts.find((d) => String(d.adm2_id) === selectedDistrict);
    const camp = camps.find((c) => String(c.adm3_id) === selectedCamp);
    const parts = [country?.country, province?.adm1, district?.adm2, camp?.adm3].filter(Boolean);
    return parts.length ? parts.join(" › ") : "No location selected";
  };

  const fetchAndShowPrompts = async () => {
    if (showPrompts) {
      setShowPrompts(false);
      return;
    }
    const params = new URLSearchParams();
    if (selectedCountry) params.append("country_id", selectedCountry);
    if (selectedProvince) params.append("adm1_id", selectedProvince);
    if (selectedDistrict) params.append("adm2_id", selectedDistrict);
    if (selectedCamp) params.append("adm3_id", selectedCamp);
    try {
      const res = await fetch(`${envConfig.API_URL}/chatbot/prompts?${params}`, {
        headers: getHeaders(),
      });
      const data = await res.json();
      setPrompts(data.categories || []);
    } catch {
      setPrompts([]);
    }
    setShowPrompts(true);
  };

  const sendMessage = async (e, overrideText) => {
    e.preventDefault();
    const text = (overrideText ?? inputText).trim();
    if (!text || loading) return;
    if (!selectedCountry) {
      alert("Please select a country first.");
      return;
    }

    const userMsg = { role: "user", text, timestamp: new Date() };
    const msgsWithUser = [...messages, userMsg];
    setMessages(msgsWithUser);
    setInputText("");
    setLoading(true);

    try {
      const payload = {
        question: text,
        country_id: parseInt(selectedCountry),
        adm1_id: selectedProvince ? parseInt(selectedProvince) : null,
        adm2_id: selectedDistrict ? parseInt(selectedDistrict) : null,
        adm3_id: selectedCamp ? parseInt(selectedCamp) : null,
      };

      const res = await fetch(`${envConfig.API_URL}/chatbot/chat`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      const loc = locationLabel();
      if (!res.ok) {
        const errMsg = data?.detail?.message || data?.detail || "An error occurred.";
        const finalMsgs = [...msgsWithUser, { role: "assistant", text: errMsg, error: true, timestamp: new Date() }];
        setMessages(finalMsgs);
        saveChat(finalMsgs);
      } else {
        const finalMsgs = [...msgsWithUser, { role: "assistant", data, locationLabel: loc, timestamp: new Date() }];
        setMessages(finalMsgs);
        saveChat(finalMsgs);
      }
    } catch {
      const finalMsgs = [...msgsWithUser, { role: "assistant", text: "Could not reach the chatbot service. Please check that it is running.", error: true, timestamp: new Date() }];
      setMessages(finalMsgs);
      saveChat(finalMsgs);
    } finally {
      setLoading(false);
    }
  };

  const fmt = (d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const campLabel = selectedCountry === "1" ? "EPA" : "Camp";

  return (
    <>
      <HeaderChatBoat />
      <section
        className="content-height"
        style={{ overflow: "hidden", minHeight: "89vh", position: "relative" }}
      >
        <div className="d-flex flex-column mt-60px" style={{ height: "90vh" }}>
          <Container fluid className="flex-grow-1 p-0" style={{ overflow: "hidden" }}>
            <Row className="h-100 g-0 flex-nowrap">

              {/* Sidebar */}
              <div
                className="border-end d-flex flex-column p-3 bg-white"
                style={{
                  position: "fixed", top: "60px", left: 0, bottom: 0,
                  width: sidebarOpen ? "350px" : "60px",
                  transition: "width 0.3s ease",
                  whiteSpace: "nowrap", overflowY: "auto", overflowX: "hidden", zIndex: 1000,
                }}
              >
                {sidebarOpen && (
                  <div className="d-flex flex-column gap-3 p-2">
                    <div className="row mt-5 sideBarBox">
                      <div className="col-12">
                        <button
                          className="btn btn-chat w-100"
                          onClick={() => {
                            setMessages([]);
                            setInputText("");
                            setShowPrompts(false);
                            chatIdRef.current = null;
                            setCurrentChatId(null);
                          }}
                          disabled={messages.length === 0 && !inputText.trim()}
                        >
                          + New Chat
                        </button>

                        {/* Location selectors */}
                        <h6 className="mt-3">
                          Location
                          {!isAdmin && <span className="text-muted small fw-normal ms-1">(assigned)</span>}
                        </h6>
                        <div className="mb-2">
                          <label className="form-label small mb-1">Country</label>
                          <select
                            className="form-select form-select-sm"
                            value={selectedCountry}
                            onChange={(e) => setSelectedCountry(e.target.value)}
                            disabled={!isAdmin && lockedLevel >= 0}
                          >
                            <option value="">Select country</option>
                            {countries.map((c) => (
                              <option key={c.country_id} value={c.country_id}>{c.country}</option>
                            ))}
                          </select>
                        </div>
                        <div className="mb-2">
                          <label className="form-label small mb-1">Province</label>
                          <select
                            className="form-select form-select-sm"
                            value={selectedProvince}
                            onChange={(e) => setSelectedProvince(e.target.value)}
                            disabled={(!selectedCountry || provinces.length === 0) || (!isAdmin && lockedLevel >= 1)}
                          >
                            <option value="">Select province</option>
                            {provinces.map((p) => (
                              <option key={p.adm1_id} value={p.adm1_id}>{p.adm1}</option>
                            ))}
                          </select>
                        </div>
                        <div className="mb-2">
                          <label className="form-label small mb-1">District</label>
                          <select
                            className="form-select form-select-sm"
                            value={selectedDistrict}
                            onChange={(e) => setSelectedDistrict(e.target.value)}
                            disabled={(!selectedProvince || districts.length === 0) || (!isAdmin && lockedLevel >= 2)}
                          >
                            <option value="">Select district</option>
                            {districts.map((d) => (
                              <option key={d.adm2_id} value={d.adm2_id}>{d.adm2}</option>
                            ))}
                          </select>
                        </div>
                        <div className="mb-3">
                          <label className="form-label small mb-1">{campLabel}</label>
                          <select
                            className="form-select form-select-sm"
                            value={selectedCamp}
                            onChange={(e) => setSelectedCamp(e.target.value)}
                            disabled={(!selectedDistrict || camps.length === 0) || (!isAdmin && lockedLevel >= 3)}
                          >
                            <option value="">Select {campLabel.toLowerCase()}</option>
                            {camps.map((c) => (
                              <option key={c.adm3_id} value={c.adm3_id}>{c.adm3}</option>
                            ))}
                          </select>
                        </div>

                        {chats.length > 0 && (
                          <>
                            <h6 className="mt-1">Recent</h6>
                            <div className="d-flex flex-column gap-1">
                              {chats.map((chat) => (
                                <div
                                  key={chat.id}
                                  className={`d-flex align-items-center justify-content-between recentChat mb-1 ${currentChatId === chat.id ? "active" : ""}`}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => loadChat(chat)}
                                >
                                  <div
                                    className="text-truncate small"
                                    style={{ maxWidth: "260px" }}
                                    title={chat.title}
                                  >
                                    {chat.title}
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm p-0 border-0 bg-transparent text-muted ms-2 flex-shrink-0"
                                    onClick={(e) => deleteChat(e, chat.id)}
                                    title="Delete conversation"
                                    style={{ fontSize: "1.1em", lineHeight: 1 }}
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                <Button
                  size="sm"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="position-absolute btn bg-white border-0"
                  style={{ right: "14px", borderRadius: "50%", zIndex: 5, width: "30px", height: "30px", padding: 0, top: "20px", boxShadow: "none" }}
                  title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
                >
                  <img src="/assets/images/Layout.png" height={20} alt="toggle" />
                </Button>
              </div>

              {/* Main Chat Area */}
              <Col
                className="d-flex flex-column"
                style={{ marginLeft: sidebarOpen ? "350px" : "60px", transition: "margin-left 0.3s ease", overflowX: "hidden" }}
              >
                {/* Messages */}
                <div className="flex-grow-1 p-3 overflow-auto" style={{ height: "72vh" }}>
                  <div className="row">
                    <div className="col-sm-12">
                      <div className="px-5 pt-3">

                        {messages.length === 0 && (
                          <div className="text-center text-muted mt-5">
                            <p>Ask about weather forecasts, advisories, or farmer/officer counts.</p>
                            <p className="small">{locationLabel()}</p>
                          </div>
                        )}

                        {messages.map((msg, i) => (
                          <div key={i}>
                            {msg.role === "user" ? (
                              <div className="d-flex justify-content-end align-items-center flex-wrap">
                                <div className="requestBox mt-3 mb-4">
                                  <div className="d-flex align-items-start justify-content-end flex-wrap">
                                    <div>
                                      <p className="mb-0 text-end">
                                        <span>{fmt(msg.timestamp)}</span>
                                        <strong className="ps-2">You</strong>
                                      </p>
                                      <div className="card border-0">
                                        <div className="card-body p-2">
                                          <p className="mb-0">{msg.text}</p>
                                        </div>
                                      </div>
                                    </div>
                                    <div className="ms-2">
                                      <p className="mb-0"><img src="/assets/images/Avatar.png" alt="" /></p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="d-flex justify-content-start align-items-center flex-wrap">
                                <div className="responseBox">
                                  <div className="d-flex align-items-start">
                                    <div className="me-2"><span className="box">BR</span></div>
                                    <div>
                                      <p className="mb-0">
                                        <strong>Blue Resilience / Ulimi Wansomba</strong>{" "}
                                        <span>{fmt(msg.timestamp)}</span>
                                      </p>
                                      <div className="card rightChatBox">
                                        <div className="card-body p-2">
                                          {msg.error ? (
                                            <p className="mb-0 text-danger">{msg.text}</p>
                                          ) : (
                                            <ResponseDisplay data={msg.data} locationLabel={msg.locationLabel} />
                                          )}
                                        </div>
                                        {/* copy / retry / dislike — not yet implemented */}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}

                        {loading && (
                          <div className="d-flex justify-content-start align-items-center flex-wrap">
                            <div className="responseBox">
                              <div className="d-flex align-items-start">
                                <div className="me-2"><span className="box">BR</span></div>
                                <p className="mb-0 text-muted fst-italic">Thinking...</p>
                              </div>
                            </div>
                          </div>
                        )}
                        <div ref={messagesEndRef} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Input */}
                <div className="bg-white mx-100px" style={{ position: "relative" }} ref={promptsRef}>

                  {/* Browse Prompts panel */}
                  {showPrompts && (
                    <div
                      className="card shadow"
                      style={{
                        position: "absolute", bottom: "100%", left: 0, right: 0,
                        zIndex: 1050, maxHeight: "320px", overflowY: "auto", marginBottom: "6px",
                      }}
                    >
                      <div className="card-body p-2">
                        {prompts.map((cat, i) => (
                          <div key={i} className="mb-2">
                            <p className="text-muted small fw-bold mb-1 px-1">
                              {cat.category}
                              {cat.note && <span className="fw-normal ms-1">— {cat.note}</span>}
                            </p>
                            {cat.questions.map((q, j) => (
                              <button
                                key={j}
                                type="button"
                                className="btn btn-sm btn-light w-100 text-start mb-1 border"
                                onClick={() => { setShowPrompts(false); sendMessage({ preventDefault: () => {} }, q); }}
                              >
                                {q}
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <form onSubmit={sendMessage}>
                    <div className="card">
                      <div className="card-body p-0">
                        <input
                          type="text"
                          className="form-control bg-white h42px border-0"
                          placeholder="Send a message..."
                          value={inputText}
                          onChange={(e) => setInputText(e.target.value)}
                          disabled={loading}
                        />
                      </div>
                      <div className="card-footer bg-white p-0">
                        <div className="d-flex justify-content-between align-items-center p-1">
                          <div>
                            <button
                              type="button"
                              className={`btn btnActionButton me-3 ${showPrompts ? "active" : ""}`}
                              onClick={fetchAndShowPrompts}
                            >
                              <span className="me-2"><img src="/assets/images/message.png" alt="" /></span>
                              Browse Prompts
                            </button>
                          </div>
                          <div>
                            <button
                              type="submit"
                              className="btn btnActionButton p-0"
                              disabled={loading || !inputText.trim()}
                            >
                              <span><img src="/assets/images/send.png" alt="send" /></span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                  <p className="mb-0 textError">Chatbot can make mistakes sometimes, please double check</p>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </section>
    </>
  );
}

export default ChatBox;
